import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ClassicPreset } from 'rete';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { Action } from '../models/action';
import { ActivateStepActionData } from '../models/activate-step-action-data';
import { ArchiveFolderActionData } from '../models/archive-folder-action-data';
import { AttributeChangedTriggerData } from '../models/attribute-change-trigger-data';
import { CallApiActionData } from '../models/call-api-action-data';
import { CompleteStepActionData } from '../models/complete-step-action-data';
import { CompleteWorkflowActionData } from '../models/complete-workflow-action-data';
import { DateReachedTriggerData } from '../models/date-reached-trigger-data';
import { ExpiredDelayTriggerData } from '../models/expire-delay-trigger-data';
import { FolderCreatedTriggerData } from '../models/folder-created-trigger-data';
import { NodePosition } from '../models/node-position';
import { ReminderCreationActionData } from '../models/reminder-creation-action-data';
import { ReteContext } from '../models/rete-context';
import { SendMailActionData } from '../models/send-mail-action-data';
import { SendSmsActionData } from '../models/send-sms-action-data';
import { Step } from '../models/step';
import { StepActivatedTriggerData } from '../models/step-activated-trigger-data';
import { StepCompletedTriggerData } from '../models/step-complete-trigger-data';
import { StepData } from '../models/step-data';
import { StepInput } from '../models/step-input';
import { StepOutput } from '../models/step-output';
import { SwitchFolderAsUnreadActionData } from '../models/switch-folder-as-unread-action-data';
import { TimerStopActionData } from '../models/timer-stop-action-data';
import { TimerTriggerData } from '../models/timer-trigger-data';
import { Trigger } from '../models/trigger';
import { AnyActionData } from '../models/Types/any-action-data';
import { AnyTriggerData } from '../models/Types/any-trigger-data';
import { UpdateAttributeActionData } from '../models/update-attribute-action-data';
import { WorkflowActionNode } from '../nodes/models/workflow-action-node';
import { WorkflowStepNode } from '../nodes/models/workflow-step-node';
import { WorkflowTriggerNode } from '../nodes/models/workflow-trigger-node';
import { DesignerCallbackHandler } from './designer-callback-handler';
import { EditorWrapper } from './editor-wrapper';
import { MappingInfos } from './mapping-infos';
import { SelectionHandler } from './selection-handler';

export class NodesManager {
    /**
     * Services utilisés dans le composant.
     */
    private toastrService = inject(ToastrService);

    /**
     * Service utilisé pour ouvrir des boites de dialogue.
     */
    public dialog: MatDialog = inject(MatDialog);

    /**
     * Contexte du designer de workflow.
     */
    context: ReteContext;

    /**
     * Gestionnaire de la selection des elements du designer de workflow.
     */
    selectionHandler: SelectionHandler;

    /**
     * Gestionnaire des évènements du designer de workflow.
     */
    callbackHandler: DesignerCallbackHandler;

    /**
     * Wrapper de l'editeur Rete.js
     */
    editorWrapper: EditorWrapper;

    /**
     * Informations sur le mapping entre l'id des noeuds et les elements correspondants.
     */
    mappingInfos: MappingInfos;

    private autogeneratedStepId = 1;

    /**
     * Ajouter un nouveau trigger au workflow.
     * @param type : Type de trigger.
     */
    public addTrigger(type: WorkflowTriggerTypes): WorkflowTriggerNode {
        switch (type) {
            case WorkflowTriggerTypes.AttributeChanged:
                return this.addTriggerNode(
                    "Mise à jour d'attribut",
                    this.editorWrapper.generateNewTriggerId(),
                    WorkflowTriggerTypes.AttributeChanged,
                    new AttributeChangedTriggerData()
                );
            case WorkflowTriggerTypes.DateReached:
                return this.addTriggerNode('Date atteinte', this.editorWrapper.generateNewTriggerId(), WorkflowTriggerTypes.DateReached, new DateReachedTriggerData());
            case WorkflowTriggerTypes.DelayExpired:
                return this.addTriggerNode('Délai expiré', this.editorWrapper.generateNewTriggerId(), WorkflowTriggerTypes.DelayExpired, new ExpiredDelayTriggerData());
            case WorkflowTriggerTypes.Timer:
                return this.addTriggerNode('Minuteur', this.editorWrapper.generateNewTriggerId(), WorkflowTriggerTypes.Timer, new TimerTriggerData());
            case WorkflowTriggerTypes.FolderCreated:
                // Un seul trigger 'Création de dossier' peut-être ajouté au designer.
                if (!this.IsExistingTriggerNode('Création de dossier')) {
                    return this.addTriggerNode(
                        'Création de dossier',
                        this.editorWrapper.generateNewTriggerId(),
                        WorkflowTriggerTypes.FolderCreated,
                        new FolderCreatedTriggerData()
                    );
                } else {
                    this.toastrService.warning('Un trigger de création de dossier est déjà présent dans le workflow', 'Trigger existant', { enableHtml: true });
                    return null;
                }
            default:
                return null;
        }
    }

    public addAction(type: WorkflowActionTypes): WorkflowActionNode {
        switch (type) {
            case WorkflowActionTypes.SendMail:
                return this.addActionNode('Envoi de mail', this.editorWrapper.generateNewActionId(), WorkflowActionTypes.SendMail, new SendMailActionData());
            case WorkflowActionTypes.SendSms:
                return this.addActionNode('Envoi de SMS', this.editorWrapper.generateNewActionId(), WorkflowActionTypes.SendSms, new SendSmsActionData());
            case WorkflowActionTypes.ApiCall:
                return this.addActionNode("Appel d'API", this.editorWrapper.generateNewActionId(), WorkflowActionTypes.ApiCall, new CallApiActionData());
            case WorkflowActionTypes.SwitchFolderAsUnread:
                return this.addActionNode(
                    'Passage du dossier en non-lu',
                    this.editorWrapper.generateNewActionId(),
                    WorkflowActionTypes.SwitchFolderAsUnread,
                    new SwitchFolderAsUnreadActionData()
                );
            case WorkflowActionTypes.UpdateAttribute:
                return this.addActionNode("Mise à jour d'attribut", this.editorWrapper.generateNewActionId(), WorkflowActionTypes.UpdateAttribute, new UpdateAttributeActionData());
            case WorkflowActionTypes.CompleteWorkflow:
                return this.addActionNode(
                    'Finalisation du workflow',
                    this.editorWrapper.generateNewActionId(),
                    WorkflowActionTypes.CompleteWorkflow,
                    new CompleteWorkflowActionData()
                );
            case WorkflowActionTypes.TimerStop:
                return this.addActionNode('Arrêt de minuteur', this.editorWrapper.generateNewActionId(), WorkflowActionTypes.TimerStop, new TimerStopActionData());
            case WorkflowActionTypes.ReminderCreation:
                return this.addActionNode('Création de rappel', this.editorWrapper.generateNewActionId(), WorkflowActionTypes.ReminderCreation, new ReminderCreationActionData());
            case WorkflowActionTypes.ArchiveFolder:
                return this.addActionNode('Archivage de dossier', this.editorWrapper.generateNewActionId(), WorkflowActionTypes.ArchiveFolder, new ArchiveFolderActionData());
            default:
                return null;
        }
    }

    public addStep(): WorkflowStepNode {
        const outputs: StepOutput[] = [];
        const inputs: StepInput[] = [];

        const stepId = this.editorWrapper.generateNewStepId();

        const stepActivatedTrigger = new Trigger(WorkflowTriggerTypes.StepActivated, new NodePosition(0, 0));
        stepActivatedTrigger.id = this.editorWrapper.generateNewTriggerId();
        stepActivatedTrigger.data = new StepActivatedTriggerData();
        stepActivatedTrigger.data.id = stepActivatedTrigger.id;
        stepActivatedTrigger.data.type = WorkflowTriggerTypes.StepActivated;
        (stepActivatedTrigger.data as StepActivatedTriggerData).stepId = stepId;

        this.context.workflow.triggers.push(stepActivatedTrigger);

        let outputStepActivatedTrigger = this.editorWrapper.createOutputSocket(stepActivatedTrigger, null);
        outputs.push(outputStepActivatedTrigger);

        const stepCompletedTrigger = new Trigger(WorkflowTriggerTypes.StepCompleted, new NodePosition(0, 0));
        stepCompletedTrigger.id = this.editorWrapper.generateNewTriggerId();
        stepCompletedTrigger.data = new StepCompletedTriggerData();
        stepCompletedTrigger.data.id = stepCompletedTrigger.id;
        stepCompletedTrigger.data.type = WorkflowTriggerTypes.StepCompleted;
        (stepCompletedTrigger.data as StepCompletedTriggerData).stepId = stepId;

        this.context.workflow.triggers.push(stepCompletedTrigger);

        let outputStepCompletedTrigger = this.editorWrapper.createOutputSocket(stepCompletedTrigger, null);
        outputs.push(outputStepCompletedTrigger);

        const activateStepAction = new Action(WorkflowActionTypes.ActivateStep, new NodePosition(0, 0));
        activateStepAction.id = this.editorWrapper.generateNewActionId();
        activateStepAction.data = new ActivateStepActionData();
        activateStepAction.data.id = activateStepAction.id;
        activateStepAction.data.type = WorkflowActionTypes.ActivateStep;
        (activateStepAction.data as ActivateStepActionData).stepId = stepId;

        this.context.workflow.actions.push(activateStepAction);

        let inputActivateStepAction = this.editorWrapper.createInputSocket(activateStepAction, null);
        inputs.push(inputActivateStepAction);

        const completeStepAction = new Action(WorkflowActionTypes.CompleteStep, new NodePosition(0, 0));
        completeStepAction.id = this.editorWrapper.generateNewActionId();
        completeStepAction.data = new CompleteStepActionData();
        completeStepAction.data.id = completeStepAction.id;
        completeStepAction.data.type = WorkflowActionTypes.CompleteStep;
        (completeStepAction.data as CompleteStepActionData).stepId = stepId;
        this.context.workflow.actions.push(completeStepAction);

        let inputCompleteStepAction = this.editorWrapper.createInputSocket(completeStepAction, null);
        inputs.push(inputCompleteStepAction);

        // TODO : génerer un nom d'étape dynamique
        let stepName = `Etape ${this.autogeneratedStepId++}`;

        const data = new StepData();
        data.id = stepId;
        data.stepName = stepName;
        data.maxDuration = 7;
        data.states = [];

        const stepNode = this.addStepNode(stepName, inputs, outputs, new NodePosition(0, 0), data);

        const stringInputs = inputs.map((x) => x.label);
        const stringOutputs = outputs.map((x) => x.label);

        const step = new Step(data.id, stepName, stringInputs, stringOutputs, new NodePosition(0, 0), data);
        this.context.workflow.steps.push(step);

        this.mappingInfos.stepNodeIdMappings.set(stepNode.id, data.id);

        this.mappingInfos.triggerNodeIdMappings.set(stepActivatedTrigger.id, stepNode.id);
        this.mappingInfos.triggerNodeIdMappings.set(stepCompletedTrigger.id, stepNode.id);
        this.mappingInfos.actionNodeIdMappings.set(activateStepAction.id, stepNode.id);
        this.mappingInfos.actionNodeIdMappings.set(completeStepAction.id, stepNode.id);
        this.mappingInfos.setStepNodeTriggerId(stepNode.id, outputStepActivatedTrigger.code, stepActivatedTrigger.id);
        this.mappingInfos.setStepNodeTriggerId(stepNode.id, outputStepCompletedTrigger.code, stepCompletedTrigger.id);
        this.mappingInfos.setStepNodeActionId(stepNode.id, inputActivateStepAction.code, activateStepAction.id);
        this.mappingInfos.setStepNodeActionId(stepNode.id, inputCompleteStepAction.code, completeStepAction.id);

        return stepNode;
    }

    /**
     * Ajoute un noeud de type trigger dans l'éditeur (Méthode générique).
     */
    private addTriggerNode(nodeName: string, triggerId: number, triggerType: WorkflowTriggerTypes, data: AnyTriggerData): WorkflowTriggerNode {
        data.id = triggerId;

        const workflowTriggerNode = this.createTriggerNodeInstance(nodeName, triggerType, data);

        workflowTriggerNode.addOutput(triggerId.toString(), new ClassicPreset.Output(new ClassicPreset.Socket('unknow'), ''));

        const trigger = new Trigger(triggerType, new NodePosition(0, 320));
        trigger.id = triggerId;
        trigger.data = data;
        this.context.workflow.addTrigger(trigger);

        // await this.context.editor.addNode(workflowTriggerNode);
        // await this.context.area.translate(workflowTriggerNode.id, { x: 0, y: 320 });

        this.mappingInfos.triggerNodeIdMappings.set(triggerId, workflowTriggerNode.id);

        this.selectionHandler.selectNode(workflowTriggerNode);

        return workflowTriggerNode;
    }

    /**
     * Ajoute un noeud de type action dans l'éditeur (Méthode générique).
     */
    private addActionNode(nodeName: string, actionId: number, actionType: WorkflowActionTypes, data: AnyActionData): WorkflowActionNode {
        data.id = actionId;
        const workflowActionNode = new WorkflowActionNode(nodeName, actionType, data, this.callbackHandler.actionNodeCallBack.bind(this.callbackHandler));

        workflowActionNode.addInput(actionId.toString(), new ClassicPreset.Input(new ClassicPreset.Socket('unknow'), ''));

        const action = new Action(actionType, new NodePosition(0, 320));
        action.id = actionId;
        action.data = data;
        this.context.workflow.addAction(action);

        // await this.context.editor.addNode(workflowActionNode);
        // await this.context.area.translate(workflowActionNode.id, { x: 0, y: 320 });

        this.mappingInfos.actionNodeIdMappings.set(actionId, workflowActionNode.id);

        this.selectionHandler.selectNode(workflowActionNode);

        return workflowActionNode;
    }

    addStepNode(stepName: string, inputs: StepInput[], outputs: StepOutput[], position: { x: number; y: number }, data: StepData): WorkflowStepNode {
        const workflowStepNode = new WorkflowStepNode(stepName, data, this.callbackHandler.stepNodeCallBack.bind(this.callbackHandler));

        inputs.forEach((input) => {
            workflowStepNode.addInput(input.code.toString(), new ClassicPreset.Input(new ClassicPreset.Socket(input.label), input.label));
        });

        outputs.forEach((output) => {
            workflowStepNode.addOutput(output.code.toString(), new ClassicPreset.Output(new ClassicPreset.Socket(output.label), output.label));
        });

        // await this.context.editor.addNode(workflowStepNode);
        // await this.context.area.translate(workflowStepNode.id, { x: position.x, y: position.y });

        this.selectionHandler.selectNode(workflowStepNode);

        console.log(`add node ${workflowStepNode.id} for step ${stepName}}`);
        return workflowStepNode;
    }

    /**
     * Vérifie si un noeud avec le nom spécifié est présent dans le workflow.
     * @param name : Nom du noeud à rechercher.
     * @returns : True : si un noeud avec ce nom existe dans le workflow; false autrement.
     */
    private IsExistingTriggerNode(name: string) {
        const nodes = this.context.editor.getNodes();
        const existingNode = nodes.find((node) => node instanceof WorkflowTriggerNode && (node as WorkflowTriggerNode).label === name);

        return existingNode;
    }

    public createTriggerNodeInstance(nodeName: string, triggerType: WorkflowTriggerTypes, data: AnyTriggerData): WorkflowTriggerNode {
        return new WorkflowTriggerNode(nodeName, triggerType, data, this.callbackHandler.triggerNodeCallBack.bind(this.callbackHandler));
    }
}
